import { useEffect, useRef, type RefObject } from 'react'

export interface UseStationSettledOptions {
  debounceMs: number
}

/**
 * Detects which carousel card is centered after a user-driven scroll has
 * settled. Uses IntersectionObserver to track each card's visibility ratio
 * + a debounced timer so we only emit `onSettled(id)` once per
 * scroll-stop, not on every frame during the swipe.
 *
 * Cards are identified by the `data-card-id` attribute on each direct
 * child of `containerRef.current`.
 */
export function useStationSettled(
  containerRef: RefObject<HTMLElement | null>,
  cardIds: ReadonlyArray<string>,
  onSettled: (id: string) => void,
  { debounceMs }: UseStationSettledOptions,
): void {
  const lastCenteredRef = useRef<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (container === null) return

    const cards = Array.from(container.querySelectorAll<HTMLElement>('[data-card-id]'))
    if (cards.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the entry with the highest intersectionRatio that's >= 0.5.
        let best: IntersectionObserverEntry | null = null
        for (const e of entries) {
          if (
            e.intersectionRatio >= 0.5 &&
            (best === null || e.intersectionRatio > best.intersectionRatio)
          ) {
            best = e
          }
        }
        if (best === null) return
        const id = (best.target as HTMLElement).getAttribute('data-card-id')
        if (id === null) return
        lastCenteredRef.current = id

        if (timerRef.current !== null) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => {
          if (lastCenteredRef.current !== null) {
            onSettled(lastCenteredRef.current)
            lastCenteredRef.current = null
          }
        }, debounceMs)
      },
      { root: container, threshold: [0, 0.5, 0.9, 1] },
    )

    for (const card of cards) {
      observer.observe(card)
    }

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
      observer.disconnect()
    }
  }, [containerRef, cardIds, onSettled, debounceMs])
}
